import * as yup from "yup";
import {
  MULTIMEDIA_MIME_TYPES,
  MULTIMEDIA_PLACEHOLDER,
  FILE_ALREADY_EXISTS,
} from "../../../../../utils/constants";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { FaCircleNotch } from "react-icons/fa";
import { IoSaveOutline } from "react-icons/io5";
import Textbox from "../../../../../components/common/Textbox";
import ReactSelect from "../../../../../components/common/ReactSelect";
import Datepicker from "../../../../../components/common/Datepicker";
import FileUpload from "../../../../../components/common/FileUpload";
import Toaster from "./../../../../../components/common/Toaster";
import contributionService from "../../../../../services/contributionService";

const schema = yup.object({
  transaction_id: yup.string(),
  amount: yup.string().required("Please enter amount."),
  period: yup.string().required("Please select period."),
  date_contributed: yup.string().required("Please select date contributed."),
  contribution_type: yup.object().shape({
    label: yup.string().required("Please select type."),
    value: yup.string().required("Please select type."),
  }),
  files: yup.mixed(),
});

const ContributionForm = ({ id, isLoading, setLoading, onSuccess }) => {
  const [fileExists, setFileExists] = useState(false);
  const [contributionTypes, setContributionTypes] = useState([]);

  const {
    register,
    handleSubmit,
    setValue,
    clearErrors,
    control,
    formState: { errors },
  } = useForm({ resolver: yupResolver(schema) });

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      await contributionService.saveAccountContributionAsync(id, data);
      Toaster.Success("Contribution Saved Successfully");
      onSuccess();
    } catch (ex) {
      if (ex.response && ex.response.data.message === FILE_ALREADY_EXISTS) {
        setFileExists(true);
      } else {
        Toaster.Error("Something went wrong.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchTypes = () => {
      const data = contributionService.getContributionTypes();
      setContributionTypes(data);
    };

    fetchTypes();
  }, []);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="p-8 space-y-6">
      <div className="grid grid-cols-2">
        <div className="col-span-2">
          <Textbox
            id="transaction-id-textbox"
            name="transaction_id"
            type="text"
            title="Transaction No."
            register={register("transaction_id")}
            errors={errors}
            optional
          />
        </div>
      </div>
      <div className="grid grid-cols-7 space-x-4">
        <div className="col-span-3">
          <ReactSelect
            id="contribution-type-dropdown"
            name="contribution_type"
            title="Contribution Type"
            options={contributionTypes}
            control={control}
            register={register("contribution_type")}
            errors={errors}
          />
        </div>
        <div className="col-span-4">
          <Textbox
            id="amount-textbox"
            name="amount"
            type="text"
            title="Amount"
            placeholder="0.00"
            register={register("amount")}
            errors={errors}
          />
        </div>
      </div>
      <div className="grid grid-cols-2 space-x-4">
        <div className="col-span-1">
          <Datepicker
            id="period-datepicker"
            name="period"
            title="Period"
            dateFormat="MMMM yyyy"
            setValue={setValue}
            clearErrors={clearErrors}
            register={register("period")}
            timeFormat={false}
            errors={errors}
            readOnly
          />
        </div>
        <div className="col-span-1">
          <Datepicker
            id="date-contributed-datepicker"
            name="date_contributed"
            title="Date Contributed"
            placeholder="MM-DD-YYYY"
            dateFormat="MM-DD-yyyy"
            setValue={setValue}
            clearErrors={clearErrors}
            register={register("date_contributed")}
            timeFormat={false}
            errors={errors}
            readOnly
          />
        </div>
      </div>
      <div className="grid grid-cols-2">
        <div className="col-span-2">
          <FileUpload
            id="files-dropdown"
            name="files"
            title="Files"
            setValue={setValue}
            clearErrors={clearErrors}
            register={register("files")}
            errors={errors}
            accepts={MULTIMEDIA_MIME_TYPES}
            placeholder={MULTIMEDIA_PLACEHOLDER}
            maxFiles={2}
            maxSize={5242880}
            multiple={true}
          />
          {fileExists && (
            <div className="pt-2">
              <span className="text-sm text-red-400">
                {FILE_ALREADY_EXISTS}
              </span>
            </div>
          )}
        </div>
      </div>
      <div className="grid grid-2 pt-2">
        <div className="col-span-2">
          <button
            type="submit"
            className="button button-primary disabled:opacity-70 disabled:cursor-not-allowed"
            disabled={isLoading}
          >
            {isLoading && <FaCircleNotch className="animate-spin mr-3" />}
            {!isLoading && <IoSaveOutline className="mr-3" />}
            Save Changes
          </button>
        </div>
      </div>
    </form>
  );
};

export default ContributionForm;
